import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { connectionCreatorApi, GetStepTypesResponse } from "api/TenantSlug/ConnectionCreator/api.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import GetLogsRequest = App.Http.Requests.ConnectionCreatorNew.GetLogsRequest;
import GetLogsResponse = Paldock.LaravelConnectionCreator.Http.Responses.GetLogsResponse;
import GetScenarioResponse = App.Http.Responses.ConnectionCreatorNew.GetScenarioResponse;
import GetScenarioRunResponse = Paldock.LaravelConnectionCreator.Http.Responses.GetScenarioRunResponse;
import GetScenarioRunsResponse = Paldock.LaravelConnectionCreator.Http.Responses.GetScenarioRunsResponse;
import GetScenariosResponse = Paldock.LaravelConnectionCreator.Http.Responses.GetScenariosResponse;
import GraphScenarioResponse = Paldock.LaravelConnectionCreator.Http.Responses.GraphScenarioResponse;
import UpdateGraphScenario = Paldock.LaravelConnectionCreator.Http.Requests.UpdateGraphScenario;
import AppStoreScenarioRequest = App.Http.Requests.ConnectionCreatorNew.AppStoreScenarioRequest;
import GetApplicableScenariosRequest = App.Http.Requests.ConnectionCreatorNew.GetApplicableScenariosRequest;
import AppUpdateScenarioRequest = App.Http.Requests.ConnectionCreatorNew.AppUpdateScenarioRequest;
import GetScenarioRunRequest = App.Http.Requests.ConnectionCreatorNew.GetScenarioRunRequest;
import GetScenarioRunsRequest = App.Http.Requests.ConnectionCreatorNew.GetScenarioRunsRequest;
import GetScenariosRequest = App.Http.Requests.ConnectionCreatorNew.GetScenariosRequest;
import GetScenarioRequest = App.Http.Requests.ConnectionCreatorNew.GetScenarioRequest;

// Query to get list of scenarios
export const useGetScenariosQuery = (request: GetScenariosRequest = {}, options = {}) => {
    return useQueryApi<GetScenariosResponse>({
        queryKey: ["getScenarios", request],
        queryFn: () => connectionCreatorApi.getScenarios(request),
        staleTime: 0, // Always refetch - scenario might have been created
        gcTime: 0, // Don't cache
        ...options,
    });
};

export const useGetApplicableScenariosQuery = (request: GetApplicableScenariosRequest, options = {}) => {
    return useQueryApi<GetScenariosResponse>({
        queryKey: ["getApplicableScenarios", request],
        queryFn: () => connectionCreatorApi.getApplicableScenarios(request),
        staleTime: 0,
        gcTime: 0,
        ...options,
    });
};

// Query to get specific scenario detail
export const useGetScenarioQuery = (scenarioId: string, request?: GetScenarioRequest, options = {}) => {
    return useQueryApi<GetScenarioResponse>({
        queryKey: ["getScenario", scenarioId, request],
        queryFn: () => connectionCreatorApi.getScenario(scenarioId, request),
        enabled: !!scenarioId,
        ...options,
    });
};

// Query to get scenario graph (steps and connections)
export const useGetScenarioGraphQuery = (scenarioId: string, options = {}) => {
    return useQueryApi<GraphScenarioResponse>({
        queryKey: ["getScenarioGraph", scenarioId],
        queryFn: () => connectionCreatorApi.getScenarioGraph(scenarioId),
        enabled: !!scenarioId,
        ...options,
    });
};

// Mutation to update scenario graph
export const useUpdateScenarioGraphMutation = (
    scenarioId: string,
    options: UseMutationOptions<GraphScenarioResponse, AxiosError<ApiErrorResponse>, UpdateGraphScenario> = {}
) => {
    return useMutationApi<GraphScenarioResponse, UpdateGraphScenario>({
        mutationKey: ["updateScenarioGraph", scenarioId],
        mutationFn: (data: UpdateGraphScenario) => connectionCreatorApi.updateScenarioGraph(scenarioId, data),
        ...options,
    });
};

// Mutation to update an existing scenario using PUT
export const useUpdateScenarioMutation = (
    scenarioId: string,
    options: UseMutationOptions<GetScenarioResponse, AxiosError<ApiErrorResponse>, AppStoreScenarioRequest> = {}
) => {
    return useMutationApi<GetScenarioResponse, AppStoreScenarioRequest>({
        mutationKey: ["updateScenario", scenarioId],
        mutationFn: (data: AppStoreScenarioRequest) => connectionCreatorApi.updateScenario(scenarioId, data),
        ...options,
    });
};

// Mutation to partially update an existing scenario using PATCH
export const usePatchScenarioMutation = (
    scenarioId: string,
    options: UseMutationOptions<GetScenarioResponse, AxiosError<ApiErrorResponse>, AppUpdateScenarioRequest> = {}
) => {
    return useMutationApi<GetScenarioResponse, AppUpdateScenarioRequest>({
        mutationKey: ["patchScenario", scenarioId],
        mutationFn: (data: AppUpdateScenarioRequest) => connectionCreatorApi.patchScenario(scenarioId, data),
        ...options,
    });
};

// Mutation to destroy a scenario
export const useDestroyScenarioMutation = (
    scenarioId: string,
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, void> = {}
) => {
    return useMutationApi<void, void>({
        mutationKey: ["destroyScenario", scenarioId],
        mutationFn: () => connectionCreatorApi.destroyScenario(scenarioId),
        ...options,
    });
};

// Mutation to create a new scenario
export const useStoreScenarioMutation = (
    options: UseMutationOptions<GetScenarioResponse, AxiosError<ApiErrorResponse>, AppStoreScenarioRequest> = {}
) => {
    return useMutationApi<GetScenarioResponse, AppStoreScenarioRequest>({
        mutationKey: ["storeScenario"],
        mutationFn: (data: AppStoreScenarioRequest) => connectionCreatorApi.storeScenario(data),
        ...options,
    });
};

// Query to get list of scenario runs
export const useGetScenarioRunsQuery = (request: GetScenarioRunsRequest = {}, options = {}) => {
    return useQueryApi<GetScenarioRunsResponse>({
        queryKey: ["getScenarioRuns", request],
        queryFn: () => connectionCreatorApi.getScenarioRuns(request),
        ...options,
    });
};

// Query to get specific scenario run detail
export const useGetScenarioRunQuery = (scenarioRunId: string, request: GetScenarioRunRequest = {}, options = {}) => {
    return useQueryApi<GetScenarioRunResponse>({
        queryKey: ["getScenarioRun", scenarioRunId, request],
        queryFn: () => connectionCreatorApi.getScenarioRun(scenarioRunId, request),
        enabled: !!scenarioRunId,
        ...options,
    });
};

// Query to get list of logs
export const useGetLogsQuery = (request: GetLogsRequest = {}, options = {}) => {
    return useQueryApi<GetLogsResponse>({
        queryKey: ["getLogs", request],
        queryFn: () => connectionCreatorApi.getLogs(request),
        ...options,
    });
};

// Query to get step types
export const useGetStepTypesQuery = (options = {}) => {
    return useQueryApi<GetStepTypesResponse>({
        queryKey: ["getStepTypes"],
        queryFn: () => connectionCreatorApi.getStepTypes(),
        ...options,
    });
};
